import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatCurrency';
import { ExecBriefing } from '../components/ExecBriefing';
import {
  Gauge, DollarSign, Activity, AlertTriangle, ShieldCheck, TrendingUp,
  GraduationCap, ArrowUpRight, ArrowDownRight, Minus, Settings2,
  ClipboardCheck, CalendarClock, ChevronRight, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RAG_STYLES = {
  green: { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10', label: 'On Track', border: 'border-l-emerald-500' },
  amber: { dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/10', label: 'Attention', border: 'border-l-amber-500' },
  red: { dot: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-500/10', label: 'Critical', border: 'border-l-rose-500' },
  nodata: { dot: 'bg-slate-300', text: 'text-muted-foreground', bg: 'bg-muted', label: 'No Data', border: 'border-l-slate-300' },
};

const DOMAIN_META = {
  financial: { name: 'Financial Health', icon: DollarSign },
  operational: { name: 'Operational Health', icon: Activity },
  risk: { name: 'Risk Health', icon: AlertTriangle },
  compliance: { name: 'Compliance Health', icon: ShieldCheck },
  pipeline: { name: 'Business Pipeline', icon: TrendingUp },
  learners: { name: 'Learner Success', icon: GraduationCap },
};

const CATEGORY_LABELS = { finance: 'Finance', projects: 'Projects', learners: 'Learners', workforce: 'Workforce', compliance: 'Compliance', bizdev: 'Business Development' };

const fmtVal = (kpi) => {
  if (kpi.value === null || kpi.value === undefined) return 'No data available';
  if (kpi.unit === 'ZAR') return formatCurrency(kpi.value);
  if (kpi.unit === '%') return `${kpi.value}%`;
  return `${kpi.value}`;
};

const MetricLine = ({ label, value }) => (
  <div className="flex justify-between text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-semibold font-mono">{value ?? '—'}</span>
  </div>
);

export default function CommandCentrePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editKpi, setEditKpi] = useState(null);
  const [form, setForm] = useState({});
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  const load = useCallback(async (silent = false) => {
    if (silent && document.hidden) return;
    try {
      const res = await axios.get(`${API}/ecc/overview`);
      setData(res.data);
    } catch (e) {
      if (!silent) toast.error('Failed to load Command Centre');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [load]);

  const saveKpi = async () => {
    try {
      await axios.put(`${API}/kpi/definitions/${editKpi.key}`, {
        target: parseFloat(form.target),
        amber_threshold: parseFloat(form.amber_threshold),
        red_threshold: parseFloat(form.red_threshold),
      });
      toast.success('KPI updated');
      setEditKpi(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="ecc-loading">
        <div className="h-10 w-96 rounded-xl bg-muted animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-36 rounded-2xl bg-muted animate-pulse" />)}
        </div>
        <div className="h-96 rounded-2xl bg-muted animate-pulse" />
      </div>
    );
  }

  const health = data?.health || {};
  const summary = data?.kpi_summary || {};
  const grouped = {};
  (data?.kpi_board || []).forEach(k => { (grouped[k.category] = grouped[k.category] || []).push(k); });

  return (
    <div className="space-y-6" data-testid="command-centre-page">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="ecc-title">
            <Gauge className="h-7 w-7 text-primary" /> Enterprise Command Centre
          </h1>
          <p className="text-muted-foreground mt-1">Organisation-wide health, KPIs, approvals and deadlines</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs" data-testid="kpi-summary-chips">
            <span className="px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold">{summary.green || 0} Green</span>
            <span className="px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold">{summary.amber || 0} Amber</span>
            <span className="px-2 py-1 rounded-full bg-rose-100 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 font-semibold">{summary.red || 0} Red</span>
          </div>
          <Button variant="outline" size="sm" className="rounded-xl" onClick={() => load()} data-testid="ecc-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* AI Morning Briefing */}
      <ExecBriefing />

      {/* Health tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {Object.entries(DOMAIN_META).map(([key, meta]) => {
          const d = health[key] || { rag: 'nodata', metrics: {} };
          const rag = RAG_STYLES[d.rag] || RAG_STYLES.nodata;
          const MIcon = meta.icon;
          const m = d.metrics || {};
          return (
            <Card key={key} onClick={() => d.drill && navigate(d.drill)}
              className={`cursor-pointer border-l-4 ${rag.border} hover:shadow-md transition-shadow !p-4`}
              data-testid={`health-tile-${key}`}>
              <CardContent className="p-0 space-y-2">
                <div className="flex items-center justify-between">
                  <MIcon className="h-4 w-4 text-muted-foreground" />
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${rag.bg} ${rag.text}`}>{rag.label}</span>
                </div>
                <p className="text-sm font-semibold leading-tight">{meta.name}</p>
                <div className="space-y-1">
                  {key === 'financial' && <>
                    <MetricLine label="Debtors" value={m.outstanding_debtors != null ? formatCurrency(m.outstanding_debtors) : null} />
                    <MetricLine label="Overdue inv." value={m.overdue_invoices} />
                  </>}
                  {key === 'operational' && <>
                    <MetricLine label="Active proj." value={m.active_projects} />
                    <MetricLine label="Overdue tasks" value={m.overdue_tasks_pct != null ? `${m.overdue_tasks_pct}%` : null} />
                  </>}
                  {key === 'risk' && <>
                    <MetricLine label="Critical" value={m.critical_risks} />
                    <MetricLine label="High" value={m.high_risks} />
                  </>}
                  {key === 'compliance' && <MetricLine label="Items tracked" value={m.total_items} />}
                  {key === 'pipeline' && <>
                    <MetricLine label="Pipeline" value={m.pipeline_value != null ? formatCurrency(m.pipeline_value) : null} />
                    <MetricLine label="Forecast" value={m.forecast != null ? formatCurrency(m.forecast) : null} />
                  </>}
                  {key === 'learners' && <MetricLine label="Active" value={m.active_learners} />}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid xl:grid-cols-3 gap-4">
        {/* KPI Board */}
        <div className="xl:col-span-2 space-y-4" data-testid="kpi-board">
          {Object.entries(grouped).map(([cat, kpis]) => (
            <Card key={cat} className="!p-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">{CATEGORY_LABELS[cat] || cat}</CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {kpis.map(k => {
                  const rag = RAG_STYLES[k.rag] || RAG_STYLES.nodata;
                  return (
                    <div key={k.key} className={`p-3 rounded-xl border ${k.rag === 'nodata' ? 'opacity-70' : ''}`} data-testid={`kpi-${k.key}`}>
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-xs font-medium text-muted-foreground truncate" title={k.description}>{k.name}</p>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className={`h-2 w-2 rounded-full ${rag.dot}`} />
                          {isAdmin && (
                            <button onClick={() => { setEditKpi(k); setForm({ target: k.target, amber_threshold: k.amber_threshold, red_threshold: k.red_threshold }); }}
                              className="text-muted-foreground hover:text-foreground" data-testid={`kpi-edit-${k.key}`}>
                              <Settings2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className={`font-bold font-mono mt-1 ${k.value === null ? 'text-sm text-muted-foreground' : 'text-lg'}`}>{fmtVal(k)}</p>
                      <div className="flex items-center justify-between mt-1 text-[11px] text-muted-foreground">
                        <span>Target: {k.unit === 'ZAR' ? formatCurrency(k.target) : `${k.target}${k.unit === '%' ? '%' : ''}`}</span>
                        {k.trend === 'up' && <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" />}
                        {k.trend === 'down' && <ArrowDownRight className="h-3.5 w-3.5 text-rose-500" />}
                        {k.trend === 'flat' && <Minus className="h-3.5 w-3.5" />}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <Card className="!p-0" data-testid="ecc-approvals-card">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-primary" /> Pending Approvals
                <Badge variant="secondary">{data?.pending_approvals?.count || 0}</Badge>
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-primary h-7" onClick={() => navigate('/approvals')} data-testid="ecc-approvals-view-all">View All</Button>
            </CardHeader>
            <CardContent className="space-y-1">
              {data?.pending_approvals?.items?.length ? data.pending_approvals.items.map(a => (
                <div key={a.id} className="flex items-center gap-2 py-2 border-b last:border-0 cursor-pointer hover:bg-muted/40 rounded-lg px-1"
                  onClick={() => navigate('/approvals')} data-testid={`ecc-approval-${a.id}`}>
                  <Badge variant="outline" className="capitalize shrink-0">{a.type}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{a.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{a.requester_name}{a.amount != null ? ` · ${formatCurrency(a.amount)}` : ''}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              )) : <p className="text-sm text-muted-foreground py-6 text-center">No pending approvals</p>}
            </CardContent>
          </Card>

          <Card className="!p-0" data-testid="ecc-deadlines-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-primary" /> Critical Deadlines (7 days)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {data?.critical_deadlines?.length ? data.critical_deadlines.map((d, i) => (
                <div key={i} className="flex items-center gap-2 py-2 border-b last:border-0 cursor-pointer hover:bg-muted/40 rounded-lg px-1"
                  onClick={() => navigate(d.link)} data-testid={`ecc-deadline-${i}`}>
                  <Badge variant="secondary" className="capitalize shrink-0">{d.type}</Badge>
                  <p className="text-sm truncate flex-1">{d.title}</p>
                  <span className="text-xs font-mono text-muted-foreground shrink-0">{d.date}</span>
                </div>
              )) : <p className="text-sm text-muted-foreground py-6 text-center">No deadlines in the next 7 days</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* KPI edit dialog */}
      <Dialog open={!!editKpi} onOpenChange={(o) => !o && setEditKpi(null)}>
        <DialogContent data-testid="kpi-edit-dialog">
          <DialogHeader>
            <DialogTitle>Configure KPI — {editKpi?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Target ({editKpi?.unit})</Label>
              <Input type="number" value={form.target ?? ''} onChange={e => setForm({ ...form, target: e.target.value })} data-testid="kpi-target-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amber threshold (% of target)</Label>
                <Input type="number" value={form.amber_threshold ?? ''} onChange={e => setForm({ ...form, amber_threshold: e.target.value })} data-testid="kpi-amber-input" />
              </div>
              <div>
                <Label>Red threshold (% of target)</Label>
                <Input type="number" value={form.red_threshold ?? ''} onChange={e => setForm({ ...form, red_threshold: e.target.value })} data-testid="kpi-red-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Attainment ≥ amber = Green · ≥ red = Amber · below = Red</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditKpi(null)}>Cancel</Button>
            <Button onClick={saveKpi} data-testid="kpi-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
